import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { uerWorkOrderChangeOtherCostSelectionListGql } from '../addWorkOrderChangeOtherQueries';

import { getInitialState } from './helpers';

export const useAddWorkOrderChangeData = ({
    workOrderChangeId,
    constructionTypeId,
}: {
    workOrderChangeId: number;
    constructionTypeId: number;
}) => {
    const [visibleRowIds, setVisibleRowIds] = useState<Record<number, boolean>>({});
    const [filterState, setFilterState] = useState<string[]>([]);

    const changeFilterHandler = useCallback(({ checkedValues }) => {
        setFilterState(checkedValues);
    }, []);

    const variables = useMemo(() => {
        if (workOrderChangeId && constructionTypeId) {
            return {
                workOrderChangeId,
                constructionTypeId,
            };
        }

        return undefined;
    }, [constructionTypeId, workOrderChangeId]);

    const [data, status] = useRequest({
        requestId: 'addWorkOrderChangeOtherData',
        query: uerWorkOrderChangeOtherCostSelectionListGql,
        withEffect: !!variables,
        variables,
    });

    const rows = useMemo(() => {
        const filteredOthers = filterState?.length
            ? data?.uerWorkOrderChangeOtherCostSelectonList.filter((other) =>
                  other?.otherCost?.name ? filterState?.includes(other?.otherCost?.name) : true,
              )
            : data?.uerWorkOrderChangeOtherCostSelectonList;

        return filteredOthers
            ?.map((other) => [
                {
                    ...other,
                    ...other?.otherCost,
                    id: other?.otherCost?.contractOtherCostId,
                    nestLevel: 0,
                },
                ...(other.workOrderChangeObjects ?? []).map((obj) => ({
                    ...obj,
                    id: `${other?.otherCost?.contractOtherCostId}_${obj?.rootObjectId?.workOrderObjectId}_${obj?.rootObjectId?.workOrderChangeObjectId}`,
                    objectId: `${obj?.rootObjectId?.workOrderObjectId}_${obj?.rootObjectId?.workOrderChangeObjectId}`,
                    parentId: other?.otherCost?.contractOtherCostId,
                })),
            ])
            .flat();
    }, [data?.uerWorkOrderChangeOtherCostSelectonList, filterState]);

    const visibleRows = useMemo(
        () =>
            rows?.filter(
                (row) =>
                    (row && 'parentId' in row && visibleRowIds[row.parentId]) ||
                    (row && 'nestLevel' in row && row?.nestLevel === 0),
            ),
        [rows, visibleRowIds],
    );

    const changeVisibleRowIds = useCallback(
        (rowId: number) => () => {
            setVisibleRowIds((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
        },
        [],
    );

    const initialState = useMemo(
        () => getInitialState(data?.uerWorkOrderChangeOtherCostSelectonList),
        [data?.uerWorkOrderChangeOtherCostSelectonList],
    );

    const isLoading = useMemo(() => status === RequestStatus.FETCHING, [status]);

    const nameFilter = useMemo<ColDef>(
        () => ({
            filter: AgGridCustomFilterAdapter,
            filterParams: {
                changeFilterHandler,
                filterItems: data?.uerWorkOrderChangeOtherCostSelectonList?.map((item) => ({
                    label: item?.otherCost?.name,
                })),
                columns: [
                    {
                        headerName: 'Наименование',
                        field: 'label',
                        checkboxSelection: true,
                        width: 400,
                    },
                ],
                isClientSideSearch: true,
                childFieldAccessor: 'label',
            },
        }),
        [changeFilterHandler, data?.uerWorkOrderChangeOtherCostSelectonList],
    );

    return useMemo(
        () => ({
            isLoading,
            groupedData: rows,
            visibleRows,
            visibleRowIds,
            changeVisibleRowIds,
            initialState,
            nameFilter,
        }),
        [isLoading, rows, visibleRows, visibleRowIds, changeVisibleRowIds, initialState, nameFilter],
    );
};
